import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SSRHeadContext } from '../../ssr/SSRHeadContext';

const SITE = 'SportsTMS';

// Also used by tenant pages, which each live on their own host, so canonicals
// must point at the host being served rather than the platform's.
const origin = () =>
  (typeof window !== 'undefined' && window.location?.origin) || 'https://sportstms.in';

/**
 * Per-page title, description, canonical and social tags.
 *
 * This writes to document.head directly rather than going through
 * react-helmet-async: that package is installed at 2.0.5 and silently emits
 * nothing in this app (no data-rh tags appear, not even for a bare
 * <Helmet><title>), and there is no second React copy to blame. Rather than
 * pin a dependency to work around it, the handful of tags we need are managed
 * here. On the marketing site the page is also rendered on the server
 * (entry-server.jsx): there the same tags are handed to SSRHeadContext and
 * written into the HTML, so crawlers that never run JavaScript still read them.
 *
 * Keep descriptions near 150 characters — Google truncates past roughly that,
 * and a cut-off sentence reads worse than a short one.
 *
 * No aggregateRating is emitted anywhere. Review markup has to reflect real,
 * collected reviews; inventing one risks a manual action and is not worth a
 * star icon in a result.
 */
const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * The tags for one page, as plain data: the browser writes them into
 * document.head, the server into the HTML it sends. Meta entries are
 * [attribute, key, content]; an entry with no content is skipped.
 */
export const buildHead = ({ pathname, title, description, image, type, noindex, schema }, base) => {
  const url = `${base}${pathname === '/' ? '' : pathname}`;
  const fullTitle = title ? `${title} | ${SITE}` : `${SITE} — Tournament Management System`;
  const social = image || `${base}/images/sportstms/hero-court.jpg`;

  return {
    title: fullTitle,
    meta: [
      ['name', 'description', description],
      ['name', 'robots', noindex ? 'noindex, follow' : 'index, follow'],
      ['property', 'og:type', type],
      ['property', 'og:site_name', SITE],
      ['property', 'og:url', url],
      ['property', 'og:title', fullTitle],
      ['property', 'og:description', description],
      ['property', 'og:image', social],
      ['property', 'og:locale', 'en_IN'],
      ['name', 'twitter:card', 'summary_large_image'],
      ['name', 'twitter:title', fullTitle],
      ['name', 'twitter:description', description],
      ['name', 'twitter:image', social],
    ],
    canonical: url,
    schema,
  };
};

const SEOHead = ({
  title,
  description,
  image,
  type = 'website',
  noindex = false,
  schema,
}) => {
  const { pathname } = useLocation();
  const ssr = useContext(SSRHeadContext);

  // Server render: effects do not run there, so hand the tags over instead.
  if (ssr) {
    ssr.head = buildHead({ pathname, title, description, image, type, noindex, schema }, ssr.origin);
  }

  useEffect(() => {
    const head = buildHead({ pathname, title, description, image, type, noindex, schema }, origin());

    document.title = head.title;
    head.meta.forEach(([attr, key, content]) => upsertMeta(attr, key, content));
    upsertLink('canonical', head.canonical);

    // One structured-data block per page, replaced rather than stacked up as
    // the visitor moves between routes.
    const existing = document.head.querySelector('script[data-seo-schema]');
    if (existing) existing.remove();
    if (schema) {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-schema', '');
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    }
  }, [pathname, title, description, image, type, noindex, schema]);

  return null;
};

/** Organisation and product markup for the home page. */
export const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'SportsTMS',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Sports tournament management',
  operatingSystem: 'Web, Android',
  description:
    'Tournament management software for sports associations and corporate leagues: registration, draws, scheduling, live scoring, standings and certificates.',
  featureList: [
    'Registration and entry approval',
    'Draw generation for groups and knockouts',
    'Scheduling with clash detection',
    'Live scoring on mobile, per sport',
    'Standings, league tables and record boards',
    'Participation and winners’ certificates',
  ],
  publisher: { '@type': 'Organization', name: 'SportsTMS' },
};

/** Builds FAQPage markup from the same list a page renders, so the two agree. */
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(([question, answer]) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

export default SEOHead;
